/**
 * Reading the access token's expiry — the single place in the app that does it.
 *
 * There used to be two independent copies, one in `AuthService` and one in
 * `jwt.interceptor`. That is a quiet hazard: the route guard decides whether to let you
 * onto a page from one copy, and the interceptor decides whether to attach your token
 * from the other. If the two ever disagreed about when a token dies you would get a route
 * that activates and then 401s on everything it loads, with nothing obviously wrong in
 * either file.
 *
 * Deliberately a plain module with no Angular dependencies: the interceptor cannot import
 * `AuthService` (AuthService needs HttpClient, whose chain includes the interceptor), so
 * the shared code has to live somewhere neither of them owns.
 */

/**
 * Milliseconds-since-epoch that this JWT expires, or null when it cannot be read —
 * malformed, unparseable base64, or missing a numeric `exp`.
 */
export function readTokenExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload?.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * True when the token is past its expiry, or unreadable.
 *
 * An unreadable token counts as expired: it cannot be used to authenticate anything, so
 * treating it as live would only produce 401s further down.
 */
export function isTokenExpired(token: string): boolean {
  const expiresAt = readTokenExpiry(token);
  return expiresAt === null || Date.now() >= expiresAt;
}
